import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Send, Bot, User, Sparkles, MessageCircle, X, AlertCircle } from 'lucide-react';
import { ChatMessage, User as UserType } from '../types';
import { projectId } from '../utils/supabase/info';
import { Alert, AlertDescription } from './ui/alert';
import { useAuth } from '../contexts/AuthContext';

interface AIAssistantProps {
  user: UserType;
}

interface ApiMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export function AIAssistant({ user }: AIAssistantProps) {
  const { accessToken } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      message: `Hello ${user.name}! I'm your AIRS AI Assistant. I have access to your real academic data from the system — including your ${user.role === 'student' ? 'profile, subjects offered for your program, and documents shared with you' : 'profile, class schedule assigned by the Admin, the list of students in your classes, and documents shared with you'}. You can ask me about your documents anytime! How can I help you today?`,
      sender: 'assistant',
      timestamp: new Date().toISOString()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const chatPanelRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Reset unread when opened
  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
      // Focus input on open (slight delay for animation)
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) setIsOpen(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen]);

  // Build conversation history for API
  const buildConversationHistory = (): ApiMessage[] => {
    return messages
      .filter(m => m.sender !== 'assistant' || m.id !== '1')
      .map(m => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.message
      })) as ApiMessage[];
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    setError(null);
    setShowQuickActions(false);

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      message: inputMessage,
      sender: 'user',
      timestamp: new Date().toISOString()
    };

    const messageToSend = inputMessage;
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      if (!accessToken) {
        throw new Error('Please log in again to use the AI assistant');
      }

      const conversationHistory = buildConversationHistory();

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-0b38861b/ai/chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            message: messageToSend,
            conversationHistory
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Server error: ${response.status}`);
      }

      const aiResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        message: data.message,
        sender: 'assistant',
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, aiResponse]);
      if (!isOpen) setUnreadCount(prev => prev + 1);
      setIsTyping(false);

    } catch (error) {
      console.error('AI chat error:', error);
      setIsTyping(false);

      const errorMessage = error instanceof Error ? error.message : 'Failed to get AI response';
      setError(errorMessage);

      const errorResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        message: `I'm sorry, I encountered an error: ${errorMessage}. Please try again or contact support if the issue persists.`,
        sender: 'assistant',
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, errorResponse]);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const quickActions = [
    user.role === 'student' ? 'Show subjects offered' : 'Show my class schedule',
    user.role === 'student' ? 'How to view subjects?' : 'Show my students list',
    'What documents are shared with me?',
    'Show my prospectus',
    'FAQs'
  ];

  return createPortal(
    <>
      {/* Backdrop overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[998] md:hidden"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Floating Chat Panel */}
      <div
        ref={chatPanelRef}
        className={`fixed z-[999] transition-all duration-300 ease-in-out
          ${isOpen
            ? 'inset-0 md:inset-auto md:bottom-6 md:right-6 md:w-[420px] md:h-[600px] md:max-h-[calc(100vh-3rem)]'
            : 'bottom-6 right-6 w-0 h-0 pointer-events-none opacity-0'
          }
        `}
      >
        <div
          className={`flex flex-col bg-background border border-border shadow-2xl overflow-hidden transition-all duration-300
            ${isOpen ? 'w-full h-full rounded-none md:rounded-2xl opacity-100 scale-100' : 'w-0 h-0 opacity-0 scale-95'}
          `}
        >
          {/* Header */}
          <div className="flex-shrink-0 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground p-3 sm:p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className="relative flex-shrink-0">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <Sparkles className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-primary" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-sm sm:text-base truncate">AIRS Assistant</h3>
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-white/20 text-primary-foreground border-0 hidden sm:inline-flex">
                      Llama 3.3
                    </Badge>
                  </div>
                  <p className="text-[11px] sm:text-xs text-primary-foreground/80">
                    Online • Ready to help
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsOpen(false)}
                  className="h-8 w-8 text-primary-foreground hover:bg-white/20 rounded-full"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-hidden relative">
            <ScrollArea className="h-full">
              <div className="space-y-3 p-3 sm:p-4">
                {error && (
                  <Alert variant="destructive" className="text-xs">
                    <AlertCircle className="h-3 w-3" />
                    <AlertDescription className="text-xs">{error}</AlertDescription>
                  </Alert>
                )}

                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex gap-2 sm:gap-3 animate-in slide-in-from-bottom-2 duration-200 ${
                      message.sender === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    {message.sender === 'assistant' && (
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 bg-primary rounded-full flex items-center justify-center shadow-sm">
                          <Bot className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary-foreground" />
                        </div>
                      </div>
                    )}

                    <div
                      className={`max-w-[85%] sm:max-w-[80%] rounded-2xl px-3 py-2.5 sm:px-4 sm:py-3 shadow-sm ${
                        message.sender === 'user'
                          ? 'bg-primary text-primary-foreground rounded-br-md'
                          : 'bg-muted rounded-bl-md'
                      }`}
                    >
                      <p className="text-[13px] sm:text-sm whitespace-pre-wrap leading-relaxed">{message.message}</p>
                      <p className={`text-[10px] sm:text-xs mt-1.5 ${
                        message.sender === 'user'
                          ? 'text-primary-foreground/60'
                          : 'text-muted-foreground'
                      }`}>
                        {formatTime(message.timestamp)}
                      </p>
                    </div>

                    {message.sender === 'user' && (
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 bg-secondary rounded-full flex items-center justify-center shadow-sm">
                          <User className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {isTyping && (
                  <div className="flex gap-2 sm:gap-3 justify-start animate-in fade-in duration-200">
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-primary rounded-full flex items-center justify-center shadow-sm">
                        <Bot className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary-foreground" />
                      </div>
                    </div>
                    <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                      <div className="flex gap-1.5 items-center">
                        <div className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>
          </div>

          {/* Quick Actions */}
          {showQuickActions && messages.length <= 1 && (
            <div className="flex-shrink-0 px-3 sm:px-4 pb-2">
              <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-1 scrollbar-hide">
                {quickActions.map((action, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setInputMessage(action);
                      inputRef.current?.focus();
                    }}
                    className="text-[11px] sm:text-xs whitespace-nowrap flex-shrink-0 rounded-full h-7 sm:h-8 px-3 hover:bg-primary/10 hover:border-primary/30 transition-colors"
                  >
                    {action}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="flex-shrink-0 border-t bg-background/95 backdrop-blur-sm p-3 sm:p-4 pb-[env(safe-area-inset-bottom,12px)] sm:pb-4">
            <div className="flex gap-2 items-end">
              <Input
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything..."
                className="flex-1 rounded-full border-muted-foreground/20 bg-muted/50 px-4 h-10 sm:h-11 text-[13px] sm:text-sm focus-visible:ring-primary/30"
                disabled={isTyping}
              />
              <Button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isTyping}
                size="icon"
                className="rounded-full h-10 w-10 sm:h-11 sm:w-11 flex-shrink-0 shadow-md transition-transform active:scale-95"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-[10px] sm:text-xs text-muted-foreground mt-2 text-center">
              Responses are based on your actual data from the system.
            </p>
          </div>
        </div>
      </div>

      {/* Floating Action Button */}
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className={`fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[997] group transition-all duration-300
          ${isOpen ? 'scale-0 opacity-0 pointer-events-none' : 'scale-100 opacity-100'}
        `}
        aria-label={isOpen ? 'Close AI Assistant' : 'Open AI Assistant'}
      >
        <div className="relative">
          {/* Pulse ring */}
          <div className="absolute inset-0 rounded-full bg-primary/30 animate-ping" />
          {/* Button */}
          <div className="relative h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-lg hover:shadow-xl flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95">
            <MessageCircle className="h-6 w-6 sm:h-7 sm:w-7" />
          </div>
          {/* Unread badge */}
          {unreadCount > 0 && (
            <div className="absolute -top-1 -right-1 min-w-[22px] h-[22px] bg-red-500 rounded-full flex items-center justify-center text-white text-[11px] font-bold shadow-md px-1 animate-in zoom-in duration-200">
              {unreadCount > 9 ? '9+' : unreadCount}
            </div>
          )}
          {/* Online indicator */}
          <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-400 rounded-full border-[2.5px] border-background shadow-sm" />
        </div>
        {/* Tooltip label */}
        <div className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-foreground text-background text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap shadow-lg">
          Chat with AIRS Assistant
          <div className="absolute top-full right-5 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-foreground" />
        </div>
      </button>
    </>,
    document.body
  );
}